import { MaterializedPath, MaterializedPathNode } from '../materializedPath'
import { shallowEqual } from '../shallowEqual'
import { ConceptValueDataType, stringToBool, XflowConcept } from '@fusion-advantage/backcompat'
import { ConceptValueRange, newConceptValueRangeFromString } from './ConceptValueRange'
import _ from 'lodash'

export type CidPathNode = MaterializedPathNode
export type CidPath = MaterializedPath<CidPathNode>

export const XFLOW_CONCEPT_MODIFIERS = [
	'description',
	'dirItemId',
	'icd10',
	'isDx',
	'isRequired',
	'primaryJumpCid',
	'title',
	'valueDataType',
	'valueRange',
]

export const XFLOW_PERMITTED_DIRITEM_MODIFIERS = [
	'icd10',
	'isDx',
	'isRequired',
	'primaryJumpCid',
	'valueDataType',
	'valueRange',
]

export interface Concept extends XflowConcept {
	// properties NOT settable by xflow attributes
	cid: string
	subconcepts: string[]
	xpaths: string[]
	cidpaths: CidPath[]

	// properties settable by xflow attributes
	description: string|null
	dirItemId: number|null
	icd10: string|null
	isDx: boolean
	isRequired: boolean
	primaryJumpCid: string|null
	title: string|null
	valueDataType: ConceptValueDataType|null
	valueRange: ConceptValueRange|null
}

export const newConcept = (cid: string, title?: string, valueDataType?: ConceptValueDataType): Concept => {
	return {
		cid,
		subconcepts: [],
		xpaths: [],
		cidpaths: [],
		description: null,
		dirItemId: null,
		isDx: false,
		isRequired: false,
		icd10: null,
		primaryJumpCid: null,
		title: title || null,
		valueDataType: valueDataType || null,
		valueRange: null,
	}
}

export const updateConceptFromXf = (concept: Concept, attributes: {[key: string]: string|null}) => {
	for(let k in attributes) {
		let v = attributes[k]
		switch(k) {
			case 'dirItemId':
				concept[k] = v ? parseInt(v) : null
				break
			case 'description':
			case 'title':
			case 'icd10':
			case 'primaryJumpCid':
				concept[k] = v
				break
			case 'isDx':
			case 'isRequired':
				concept[k] = stringToBool(v)
				break
			case 'valueDataType':
				if(Object.values(ConceptValueDataType).some((m: string) => m === v)) {
					concept[k] = <ConceptValueDataType> v
				}
				break
			case 'valueRange':
				concept[k] = newConceptValueRangeFromString(v)
				break
		}
	}
}

export const testConceptXfAttributeConflict = (concept: Concept, attributeName: string, attributeValue: string|null): boolean => {
	const k = attributeName,
	      v = attributeValue
	switch(k) {
		case 'description':
		case 'title':
		case 'icd10':
		case 'primaryJumpCid':
			return concept[k] !== v
		case 'isDx':
		case 'isRequired':
			return concept[k] !== stringToBool(v)
		case 'valueDataType':
			if(Object.values(ConceptValueDataType).some((m: string) => m === v)) {
				return concept[k] !== <ConceptValueDataType> v
			}
			break
		case 'valueRange':
			return !shallowEqual(concept[k], newConceptValueRangeFromString(v))
	}
	return false
}

export interface ConceptsByCid {
	[cid: string]: Concept
}
